// AskScopeBar — the /ask scope chip row + facet picker (S4-3, spec 2026-07-31-query-spa-surface
// §4.1-4.3, D33's "sticky scope with the visible chip row").
//
// Renders above the composer in BOTH composer homes (empty state + dock) so pinned scope is visible
// in the same glance as the question — "nothing is silent" (§4.3). Two chip origins:
//   pinned   — user-set, lives in useAskQuery thread state, X removes it from the NEXT request;
//   inferred — the planner's own facets off the LAST turn's scope frame, "auto"-styled; X is a
//              display dismissal + the §8 telemetry event (the planner re-infers per question —
//              there is nothing to un-send until the Q4 relaxation ladder ships).
//
// The picker offers ONLY what QUERY_FACET_CAPABILITIES admits (via the facet inventory) — a chip
// the server would 400 or silently drop must not be offerable. Today that is a single site and
// `since`; S4-9 widens it with the M2 apply. Typed-ahead is deterministic prefix/token matching
// over a KNOWN list (legitimate under the no-shortcut rule; free-text facet extraction is Q1/Q2's
// LLM job, never a regex here).
import { useRef, useState } from 'react';
import type { QueryScope } from '@terum/contracts';
import { useAskFacets } from '../../hooks/useAskFacets';
import { useEscapeToClose } from '../../hooks/useEscapeToClose';
import { track } from '../../lib/analytics';
import {
  chipKey,
  deriveChips,
  isCommittableSinceDay,
  matchFacetRows,
  pinSince,
  pinSite,
  unpin,
  utcDayAgo,
  type ScopeChip,
  type ScopeFrame,
} from '../../lib/ask-scope';
import { siteLabel } from '../../lib/sites';

interface AskScopeBarProps {
  scope: QueryScope | undefined;
  setScope: (next: QueryScope | undefined) => void;
  lastFrame: ScopeFrame | null | undefined; // the LAST turn's scope frame (inferred/relaxed/unresolved)
  dismissedInferred: ReadonlySet<string>;
  dismissInferred: (key: string) => void;
  disabled: boolean; // while streaming — scope applies to the NEXT question, but mid-stream edits confuse
}

const SINCE_PRESETS = [
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 30 days', days: 30 },
  { label: 'Last 90 days', days: 90 },
] as const;

export function AskScopeBar({ scope, setScope, lastFrame, dismissedInferred, dismissInferred, disabled }: AskScopeBarProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  // The inventory is fetched only once the picker opens (~28 chunked reads server-side) and cached
  // 5 min — not on every composer mount.
  const facets = useAskFacets(open);
  useEscapeToClose(() => setOpen(false), open);

  const chips = deriveChips(scope, lastFrame, dismissedInferred);

  const removeChip = (c: ScopeChip) => {
    // STRUCTURE-ONLY telemetry (analytics.ts invariant): facet name + origin + (for sites) the enum
    // id. Never a date value, person name, or project id. This event is the §8 measurement — whether
    // an inferred chip is ever removed in real use is the honest test of the pinned/inferred design.
    track('ask_scope_chip_removed', {
      facet: c.facet,
      origin: c.origin,
      ...(c.facet === 'sites' ? { site: c.value } : {}),
    });
    if (c.origin === 'pinned') setScope(unpin(scope, c.facet, c.value));
    else dismissInferred(chipKey(c));
  };

  const pickSite = (id: string) => {
    const capability = facets.data?.capabilities.sites === 'multi' ? 'multi' : 'single';
    setScope(pinSite(scope, id, capability));
    setOpen(false);
    setQuery('');
  };

  const pickSince = (isoDay: string) => {
    if (!isoDay) return;
    setScope(pinSince(scope, isoDay));
    setOpen(false);
    setQuery('');
  };

  // Site rows the picker offers: entitled-corpus sites, labeled by the SPA's one label map. The
  // already-pinned site is shown as active rather than hidden — picking it again is a no-op swap.
  const siteRows = (facets.data?.sites ?? []).map((s) => ({ id: s.id, label: siteLabel(s.id), count: s.count }));
  const matchedSites = matchFacetRows(siteRows, query);
  const sinceCapable = facets.data?.capabilities.since !== false;

  return (
    <div className="ask-scope" data-disabled={disabled ? '' : undefined}>
      {chips.map((c) => (
        <span
          key={`${c.origin}:${chipKey(c)}`}
          className={`ask-scope-chip${c.origin === 'inferred' ? ' auto' : ''}${c.relaxed ? ' relaxed' : ''}${c.unresolved ? ' unresolved' : ''}`}
          title={
            c.unresolved
              ? `"${c.value}" matched nothing in your corpus. The answer was NOT silently widened`
              : c.relaxed
                ? 'The engine dropped this filter mid-run because it matched nothing'
                : c.origin === 'inferred'
                  ? 'Added automatically for this answer'
                  : undefined
          }
        >
          {c.origin === 'inferred' && <span className="ask-scope-auto-tag">auto</span>}
          {c.label}
          <button
            className="ask-scope-x"
            onClick={() => removeChip(c)}
            disabled={disabled}
            aria-label={`Remove ${c.label} filter`}
          >
            ×
          </button>
        </span>
      ))}
      <span className="ask-scope-anchor">
        <button
          className="ask-scope-add"
          onClick={() => {
            setOpen((v) => !v);
            setTimeout(() => inputRef.current?.focus(), 0);
          }}
          disabled={disabled}
          aria-expanded={open}
          aria-label="Add a filter"
        >
          ＋ Filter
        </button>
        {open && (
          <div className="ask-scope-panel" role="dialog" aria-label="Add a filter">
            <input
              ref={inputRef}
              className="ask-scope-search"
              placeholder="Filter sources…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            {facets.isLoading && <div className="ask-scope-note">Loading your sources…</div>}
            {facets.isError && <div className="ask-scope-note">Filters are unavailable right now.</div>}
            {facets.data && (
              <>
                {matchedSites.length > 0 && (
                  <div className="ask-scope-group">
                    <div className="ask-scope-group-label">Source</div>
                    {matchedSites.map((s) => (
                      <button
                        key={s.id}
                        className={`ask-scope-row${scope?.sites?.includes(s.id) ? ' active' : ''}`}
                        onClick={() => pickSite(s.id)}
                      >
                        <span>{s.label}</span>
                        <span className="ask-scope-count">{s.count}</span>
                      </button>
                    ))}
                  </div>
                )}
                {sinceCapable && (
                  <div className="ask-scope-group">
                    <div className="ask-scope-group-label">Since</div>
                    <div className="ask-scope-presets">
                      {SINCE_PRESETS.map((p) => (
                        <button key={p.days} className="ask-scope-preset" onClick={() => pickSince(utcDayAgo(p.days, Date.now()))}>
                          {p.label}
                        </button>
                      ))}
                    </div>
                    {/* A date input fires change on every INTERMEDIATE value — in M/D/Y locales the
                        first year keystroke is a complete-but-garbage date (year 0002) that would pin
                        and unmount the picker mid-typing. isCommittableSinceDay holds the commit until
                        the year is plausible; the calendar widget commits in one event as before. */}
                    <input
                      className="ask-scope-date"
                      type="date"
                      min="1900-01-01"
                      aria-label="Since date"
                      onChange={(e) => {
                        if (isCommittableSinceDay(e.target.value)) pickSince(e.target.value);
                      }}
                    />
                  </div>
                )}
                {matchedSites.length === 0 && query && (
                  <div className="ask-scope-note">Nothing matches “{query}”.</div>
                )}
              </>
            )}
          </div>
        )}
      </span>
    </div>
  );
}
